import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, CalendarDays, Martini, CheckCircle2, Loader2, ArrowRight, AlertTriangle } from 'lucide-react';
import api from '../api/client';

/**
 * AperitivoSection — Aperitivo Nymia (Phase 77).
 * Shared block rendered mid-page on BOTH landing pages
 * (/join-community and /call). Content + status come from a single
 * admin-managed config (GET /api/aperitivo/config):
 *   - active  → booking form, confirmation email
 *   - soldout → same form, waitlist CTA + waitlist email
 *   - hidden  → the whole section disappears
 */

const GOLD = '#D4AF37';
const easeCine = [0.16, 1, 0.3, 1];
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: easeCine },
  }),
};

const Serif = ({ children, className = '' }) => (
  <span className={className} style={{ fontFamily: "'Playfair Display', serif" }}>{children}</span>
);

const PROFESSIONS = ['Psicologo/a', 'Customer Care o Call Center', 'Utente', 'Voce Pro', 'Altro'];

const inputCls =
  'w-full rounded-xl border border-white/15 bg-white/[0.04] px-4 py-3 text-[15px] text-white placeholder-white/35 outline-none transition-colors focus:border-[#D4AF37]/60';

const AperitivoSection = ({ source }) => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '', profession: '', profession_other: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);   // { message }
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/aperitivo/config').then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, []);

  if (!cfg || cfg.status === 'hidden') return null;

  const soldout = cfg.status === 'soldout';
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError('');
    if (!form.full_name.trim() || !form.email.trim() || !form.profession) {
      setError('Compila tutti i campi obbligatori.');
      return;
    }
    if (form.profession === 'Altro' && !form.profession_other.trim()) {
      setError('Specifica la tua professione.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/aperitivo/book', { ...form, source, website: '' });
      setDone({ message: data.message });
    } catch (err) {
      setError(err.response?.data?.detail || 'Si è verificato un errore. Riprova.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="relative overflow-hidden border-y border-[#D4AF37]/25 py-20 md:py-28"
      style={{ background: 'radial-gradient(ellipse at 50% 0%, rgba(212,175,55,0.10) 0%, rgba(5,5,5,0) 60%)' }}
      data-testid="aperitivo-section"
    >
      <div className="mx-auto max-w-3xl px-5">
        {/* Intestazione */}
        <div className="text-center">
          <motion.span
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
            className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em]"
            style={{ color: GOLD }}
          >
            <Martini className="h-3.5 w-3.5" /> Aperitivo Nymia
          </motion.span>
          <motion.h2
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1}
            className="text-3xl leading-tight sm:text-4xl" data-testid="aperitivo-title"
          >
            <Serif>{cfg.title}</Serif>
          </motion.h2>
          <motion.p
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={2}
            className="mt-4 text-lg font-semibold text-white/90 md:text-xl" data-testid="aperitivo-subtitle"
          >
            {cfg.subtitle}
          </motion.p>
          <motion.span
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={3}
            className="mt-4 inline-block rounded-full border border-emerald-400/40 bg-emerald-500/10 px-4 py-1.5 text-[13px] font-bold text-emerald-300"
            data-testid="aperitivo-badge"
          >
            {cfg.badge}
          </motion.span>
        </div>

        {/* Dettagli evento */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={4}
          className="mx-auto mt-8 grid max-w-xl gap-3 sm:grid-cols-2" data-testid="aperitivo-details"
        >
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5">
            <MapPin className="h-5 w-5 shrink-0" style={{ color: GOLD }} strokeWidth={1.8} />
            <div className="min-w-0 text-left">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-white/45">Dove</p>
              <p className="text-sm font-semibold text-white/90">{cfg.venue} – {cfg.address}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5">
            <CalendarDays className="h-5 w-5 shrink-0" style={{ color: GOLD }} strokeWidth={1.8} />
            <div className="min-w-0 text-left">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-white/45">Quando</p>
              <p className="text-sm font-semibold text-white/90">{cfg.event_date}, dalle {cfg.time_start} alle {cfg.time_end}</p>
            </div>
          </div>
        </motion.div>

        {/* Testo invito */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={5}
          className="mx-auto mt-7 max-w-xl space-y-2.5 text-center text-[15px] leading-relaxed text-white/70"
          data-testid="aperitivo-invite-text"
        >
          {(cfg.invite_text || '').split('\n').filter(Boolean).map((line, i) => <p key={i}>{line}</p>)}
        </motion.div>

        {/* Form / messaggio finale */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={6}
          className="mx-auto mt-9 max-w-xl rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-8"
        >
          {done ? (
            <div className="space-y-2 py-4 text-center" data-testid="aperitivo-success-message">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" strokeWidth={1.5} />
              {done.message.split('\n').filter(Boolean).map((line, i) => (
                <p key={i} className={i === 0 ? 'text-lg font-bold text-white' : 'text-[15px] text-white/75'}>{line}</p>
              ))}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="aperitivo-form">
              <input
                type="text" value={form.full_name} onChange={set('full_name')}
                placeholder="Nome e Cognome *" required maxLength={120}
                className={inputCls} data-testid="aperitivo-name-input"
              />
              <input
                type="email" value={form.email} onChange={set('email')}
                placeholder="Email *" required
                className={inputCls} data-testid="aperitivo-email-input"
              />
              <input
                type="tel" value={form.phone} onChange={set('phone')}
                placeholder="Telefono (facoltativo)" maxLength={40}
                className={inputCls} data-testid="aperitivo-phone-input"
              />
              <select
                value={form.profession} onChange={set('profession')} required
                className={`${inputCls} appearance-none ${form.profession ? 'text-white' : 'text-white/35'}`}
                style={{ colorScheme: 'dark' }}
                data-testid="aperitivo-profession-select"
              >
                <option value="" disabled>Professione o percorso di studi *</option>
                {PROFESSIONS.map((p) => <option key={p} value={p} className="bg-[#0a0a0a] text-white">{p}</option>)}
              </select>

              {form.profession === 'Altro' && (
                <div>
                  <label className="mb-1.5 block text-[13px] font-medium text-white/70" htmlFor="aperitivo-profession-other">
                    Specifica la tua professione *
                  </label>
                  <input
                    id="aperitivo-profession-other"
                    type="text"
                    value={form.profession_other}
                    onChange={set('profession_other')}
                    placeholder="Es. Musicista, Studente di Economia…"
                    maxLength={80}
                    required
                    className={inputCls}
                    data-testid="aperitivo-profession-other-input"
                  />
                </div>
              )}

              {error && (
                <p className="flex items-start gap-2 rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-3 text-[13px] font-medium text-amber-300" data-testid="aperitivo-error-message">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
                </p>
              )}

              <button
                type="submit" disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 text-base font-bold text-black shadow-[0_8px_40px_rgba(212,175,55,0.35)] transition-transform hover:scale-[1.02] disabled:opacity-60"
                style={{ backgroundColor: GOLD }}
                data-testid="aperitivo-submit-btn"
              >
                {busy
                  ? <Loader2 className="h-5 w-5 animate-spin" />
                  : (<>{soldout ? "Iscriviti alla lista d'attesa" : 'Prenota il tuo posto'} <ArrowRight className="h-5 w-5" /></>)}
              </button>
            </form>
          )}

          <Link
            to="/contact"
            className="mt-4 flex w-full items-center justify-center rounded-full border border-white/15 px-8 py-3 text-sm font-semibold text-white/85 transition-colors hover:border-white/40"
            data-testid="aperitivo-contact-btn"
          >
            Contattaci
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default AperitivoSection;
